import type { AppData, Lot, Product, Purchase } from "./types";
import { convert } from "./format";

const now = new Date();

function isoDate(daysAgo: number): string {
  const d = new Date(now);
  d.setDate(d.getDate() - daysAgo);
  return d.toISOString().slice(0, 10);
}

function isoDateTime(daysAgo: number): string {
  const d = new Date(now);
  d.setDate(d.getDate() - daysAgo);
  return d.toISOString();
}

function inDays(days: number): string {
  const d = new Date(now);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

export function defaultSettings(): AppData["settings"] {
  return {
    presentationCurrency: "DOP",
    exchangeRate: 60,
    theme: "light",
    businessName: "Mi negocio",
  };
}

/**
 * Datos de ejemplo basados en el caso funcional de las bocinas,
 * ampliados con más productos para que la app se vea completa desde el inicio.
 */
export function buildSeedData(): AppData {
  const productBocinaGrande: Product = {
    id: "prod-bocina-grande",
    name: "Bocina grande",
    category: "Audio",
    unit: "unidad",
    defaultSalePrice: 15,
    currency: "DOP",
    createdAt: isoDateTime(30),
  };
  const productBocinaPequena: Product = {
    id: "prod-bocina-pequena",
    name: "Bocina pequeña",
    category: "Audio",
    unit: "unidad",
    defaultSalePrice: 8,
    currency: "DOP",
    createdAt: isoDateTime(30),
  };
  const productAudifonos: Product = {
    id: "prod-audifonos",
    name: "Audífonos Bluetooth",
    category: "Accesorios",
    unit: "unidad",
    defaultSalePrice: 450,
    currency: "DOP",
    createdAt: isoDateTime(22),
  };
  const productCargador: Product = {
    id: "prod-cargador",
    name: "Cargador USB-C",
    category: "Accesorios",
    unit: "unidad",
    defaultSalePrice: 180,
    currency: "DOP",
    createdAt: isoDateTime(18),
  };

  const products = [productBocinaGrande, productBocinaPequena, productAudifonos, productCargador];

  const purchase1: Purchase = {
    id: "purchase-1",
    type: "purchase",
    date: isoDate(10),
    supplier: "Distribuidora del Caribe",
    invoiceNumber: "F-0012",
    currency: "DOP",
    exchangeRate: null,
    notes: "Compra de bocinas para la temporada.",
    documentIds: [],
    lotIds: ["lot-bg-1", "lot-bp-1"],
    createdAt: isoDateTime(10),
  };

  const purchase2: Purchase = {
    id: "purchase-2",
    type: "purchase",
    date: isoDate(6),
    supplier: "Tecno Import",
    invoiceNumber: "TI-3391",
    currency: "DOP",
    exchangeRate: null,
    notes: "",
    documentIds: [],
    lotIds: ["lot-aud-1"],
    createdAt: isoDateTime(6),
  };

  const purchase3: Purchase = {
    id: "purchase-3",
    type: "purchase",
    date: isoDate(4),
    supplier: "Tecno Import",
    invoiceNumber: "TI-3402",
    currency: "DOP",
    exchangeRate: null,
    notes: "Cargadores para reventa.",
    documentIds: [],
    lotIds: ["lot-car-1"],
    createdAt: isoDateTime(4),
  };

  const lots: Lot[] = [
    {
      id: "lot-bg-1",
      productId: productBocinaGrande.id,
      productName: productBocinaGrande.name,
      purchaseId: purchase1.id,
      quantity: 5,
      unitCost: 10,
      salePrice: 15,
      currency: "DOP",
      expirationDate: null,
      purchaseDate: isoDate(10),
    },
    {
      id: "lot-bp-1",
      productId: productBocinaPequena.id,
      productName: productBocinaPequena.name,
      purchaseId: purchase1.id,
      quantity: 15,
      unitCost: 5,
      salePrice: 8,
      currency: "DOP",
      expirationDate: null,
      purchaseDate: isoDate(10),
    },
    {
      id: "lot-aud-1",
      productId: productAudifonos.id,
      productName: productAudifonos.name,
      purchaseId: purchase2.id,
      quantity: 10,
      unitCost: 250,
      salePrice: 450,
      currency: "DOP",
      expirationDate: inDays(18),
      purchaseDate: isoDate(6),
    },
    {
      id: "lot-car-1",
      productId: productCargador.id,
      productName: productCargador.name,
      purchaseId: purchase3.id,
      quantity: 30,
      unitCost: 90,
      salePrice: 180,
      currency: "DOP",
      expirationDate: null,
      purchaseDate: isoDate(4),
    },
  ];

  const sale1 = {
    id: "sale-1",
    type: "sale" as const,
    date: isoDate(3),
    customer: "Cliente mostrador",
    paymentMethod: "Efectivo",
    currency: "DOP" as const,
    exchangeRate: null,
    discount: 0,
    notes: "Venta de prueba del caso funcional.",
    documentIds: [],
    items: [
      {
        id: "saleitem-1",
        productId: productBocinaGrande.id,
        productName: productBocinaGrande.name,
        quantity: 2,
        unitPrice: 15,
        costOfGoods: 20,
        consumptions: [{ lotId: "lot-bg-1", quantity: 2, unitCost: 10 }],
      },
    ],
    createdAt: isoDateTime(3),
  };

  const sale2 = {
    id: "sale-2",
    type: "sale" as const,
    date: isoDate(2),
    customer: "Sra. Méndez",
    paymentMethod: "Transferencia",
    currency: "DOP" as const,
    exchangeRate: null,
    discount: 50,
    notes: "",
    documentIds: [],
    items: [
      {
        id: "saleitem-2",
        productId: productAudifonos.id,
        productName: productAudifonos.name,
        quantity: 3,
        unitPrice: 450,
        costOfGoods: 750,
        consumptions: [{ lotId: "lot-aud-1", quantity: 3, unitCost: 250 }],
      },
    ],
    createdAt: isoDateTime(2),
  };

  const expenses = [
    {
      id: "expense-1",
      type: "expense" as const,
      date: isoDate(9),
      category: "Transporte",
      description: "Flete de la mercancía",
      amount: 300,
      currency: "DOP" as const,
      exchangeRate: null,
      notes: "",
      linkedPurchaseId: purchase1.id,
      documentIds: [],
      createdAt: isoDateTime(9),
    },
    {
      id: "expense-2",
      type: "expense" as const,
      date: isoDate(5),
      category: "Combustible",
      description: "Gasolina para reparto",
      amount: 500,
      currency: "DOP" as const,
      exchangeRate: null,
      notes: "",
      linkedPurchaseId: null,
      documentIds: [],
      createdAt: isoDateTime(5),
    },
    {
      id: "expense-3",
      type: "expense" as const,
      date: isoDate(1),
      category: "Empaque",
      description: "Bolsas y etiquetas",
      amount: 220,
      currency: "DOP" as const,
      exchangeRate: null,
      notes: "",
      linkedPurchaseId: null,
      documentIds: [],
      createdAt: isoDateTime(1),
    },
  ];

  const returns = [
    {
      id: "return-1",
      type: "return" as const,
      date: isoDate(1),
      reason: "to_supplier" as const,
      productId: productCargador.id,
      productName: productCargador.name,
      quantity: 2,
      unitCost: 90,
      amount: 180,
      currency: "DOP" as const,
      exchangeRate: null,
      refundPending: 180,
      refundReceived: false,
      restock: false,
      removesInventory: true,
      counterparty: "Tecno Import",
      notes: "Cargadores defectuosos devueltos al proveedor.",
      documentIds: [],
      lotId: "lot-car-1",
      createdAt: isoDateTime(1),
    },
  ];

  return {
    products,
    lots,
    purchases: [purchase1, purchase2, purchase3],
    sales: [sale1, sale2],
    expenses,
    returns,
    documents: [],
    adjustments: [],
    settings: defaultSettings(),
  };
}

export function emptyData(): AppData {
  return {
    products: [],
    lots: [],
    purchases: [],
    sales: [],
    expenses: [],
    returns: [],
    documents: [],
    adjustments: [],
    settings: defaultSettings(),
  };
}